import { Button } from '@eb/eds-button';
import {
    CONSUMER_CARD_DEFAULTS,
    EventCard,
    FormattedEvent,
    ShareOptionsProps,
} from '@eb/event-renderer';
import { gettext } from '@eb/i18n';
import React from 'react';
import { SettingsContext } from '../../app';
import {
    label,
    shareType,
    trackShareAction,
} from '../../tracking/trackShareAction';
import { AffiliateCodes, GACodes } from '../../types';
import { RelatedEventsChildrenProps } from './RelatedEventsRoot';

const Heading = ({
    isCreatorCollectionEvent,
}: {
    isCreatorCollectionEvent: boolean;
}): JSX.Element => (
    <h3 className="eds-text--center eds-text-hs eds-l-mar-bot-6">
        {isCreatorCollectionEvent
            ? gettext('More events in this collection')
            : gettext('More events from this organizer')}
    </h3>
);

const EventCardList = ({
    isAuthenticated,
    events,
    shareComponentProps,
    affCode,
}: RelatedEventsChildrenProps & { affCode: string }): JSX.Element => (
    <div data-testid="event-card-list-more-from">
        {events?.map((event: FormattedEvent) => {
            const shareEventProps: ShareOptionsProps = {
                eventId: event.id,
                eventName: event.name,
                eventUrl: event.url,
                ...shareComponentProps,
            };

            return (
                <EventCard
                    {...CONSUMER_CARD_DEFAULTS}
                    affCode={affCode}
                    eventData={event}
                    key={event.id}
                    gaCategory="related-events"
                    isAuthenticated={isAuthenticated}
                    type="list"
                    shareOptions={shareEventProps}
                    onDidShareClick={(id: shareType) =>
                        trackShareAction(
                            label(id),
                            GACodes.GA_CATEGORY_EVENT_CARD,
                        )
                    }
                    onWillShareClick={() =>
                        trackShareAction(
                            GACodes.GA_LABEL_OPEN_MODAL,
                            GACodes.GA_CATEGORY_EVENT_CARD,
                        )
                    }
                />
            );
        })}
    </div>
);

const ViewCreatorCollectionLink = (): JSX.Element | null => (
    <div className="eds-l-mar-vert-4 eds-align--center-horizontal">
        <Button type="link" href="/cc/1234" target="_blank">
            {gettext('View collection')}
        </Button>
    </div>
);

const MoreEventsFrom = (
    props: RelatedEventsChildrenProps,
): JSX.Element | null => {
    const { isAuthenticated, shareComponentProps, events } = props;
    const {
        featureFlags: { launchLinkToCreatorCollectionsOnListing = false } = {},
    } = React.useContext(SettingsContext);
    // TODO: Should add something that evaluates if the event belongs to some creator collection
    const isCreatorCollectionEvent = launchLinkToCreatorCollectionsOnListing;
    const affCode = isCreatorCollectionEvent
        ? AffiliateCodes.AFF_RELATED_EVENTS_SAME_COLLECTION
        : AffiliateCodes.AFF_RELATED_EVENTS_SAME_ORG;

    if (!events || events.length <= 0) {
        return null;
    }

    return (
        <div className="eds-g-group">
            <div className="eds-g-cell eds-g-cell--has-overflow eds-g-cell-12-12 eds-g-cell-mn-8-12 eds-g-offset-mn-2-12 eds-l-pad-top-8 eds-l-pad-hor-5">
                <Heading isCreatorCollectionEvent={isCreatorCollectionEvent} />
                <EventCardList
                    {...{
                        isAuthenticated,
                        events,
                        shareComponentProps,
                        affCode,
                    }}
                />
                {isCreatorCollectionEvent ? (
                    <ViewCreatorCollectionLink />
                ) : null}
            </div>
        </div>
    );
};

export default MoreEventsFrom;
